import { LazyMotion, MotionConfig } from 'motion/react';
import { type ReactNode } from 'react';

/**
 * Deferred so the features arrive on an idle network after the page is
 * interactive, not in the critical path. Until they land, every `m` element
 * renders as the plain DOM node it wraps — which is exactly what the
 * prerendered HTML contains.
 */
const loadFeatures = () => import('./motionFeatures').then((mod) => mod.default);

/**
 * The one place the animation runtime is configured (§52).
 *
 * `strict` makes the full `motion.*` components throw, so every animated
 * element on the site has to be the lightweight `m.*` one and nobody can
 * quietly pull the whole library back into the entry bundle.
 *
 * `reducedMotion="user"` disables transforms for a visitor who has asked for
 * less motion, while leaving opacity alone — the same bargain the CSS reveals
 * already make, now applied to every `m` element without each one asking.
 *
 * Motion is used here only for things that cannot hide content: hover and tap
 * feedback, and elements that animate as they mount. Scroll entrances stay with
 * `Reveal` / `RevealGroup`, because those are visible in the prerendered HTML
 * and only hide themselves once JavaScript is running; an `initial={{opacity:0}}`
 * would ship `opacity: 0` in the HTML and leave the band blank if the script
 * never arrives.
 */
export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={loadFeatures} strict>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LazyMotion>
  );
}
